import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import MultiCampaignReport from './MultiCampaignReport';
import reportService from '../services/reportService';

const ReportViewer = () => {
  const [reportData, setReportData] = useState(null);
  const [reportName, setReportName] = useState('Multi-Campaign Report');
  const [savedReportId, setSavedReportId] = useState(null);
  const [initialAiInsights, setInitialAiInsights] = useState(null);
  const [error, setError] = useState(null);
  const [pdfCustomisation, setPdfCustomisation] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get('id');

    if (idParam) {
      // Opened from "Recent Reports" — fetch full report from API
      setSavedReportId(idParam);
      reportService.getSavedReport(idParam).then(result => {
        if (!result.success || !result.data) {
          setError('Report not found or could not be loaded.');
          return;
        }
        const { reportData: rd, title, aiInsights, config } = result.data;
        setReportData(rd);
        if (title) setReportName(title);
        if (aiInsights) setInitialAiInsights(aiInsights);
        if (config?.pdfOptions) setPdfCustomisation(config.pdfOptions);
      });
      return;
    }

    // Opened from "Open in New Tab" — read from sessionStorage
    try {
      const raw = sessionStorage.getItem('mcReportData');
      const name = sessionStorage.getItem('mcReportName');
      const savedId = sessionStorage.getItem('mcSavedReportId');
      const aiRaw = sessionStorage.getItem('mcSavedAiInsights');
      const customRaw = sessionStorage.getItem('mcReportCustomisation');
      if (!raw) {
        setError('No report data found. Please generate a report first.');
        return;
      }
      setReportData(JSON.parse(raw));
      if (name) setReportName(name);
      if (savedId) setSavedReportId(savedId);
      if (aiRaw) setInitialAiInsights(JSON.parse(aiRaw));
      if (customRaw) setPdfCustomisation(JSON.parse(customRaw));
    } catch (e) {
      setError('Failed to load report data.');
    }
  }, []);

  const formatPercentDisplay = (v) => `${Number(v || 0).toFixed(2)}%`;
  const formatNumberDisplay = (v) => Number(v || 0).toLocaleString();
  const percentValue = (v) => Number(v || 0);

  return (
    <div style={{ minHeight: '100vh', background: '#F0F4F8' }}>
      {/* Top header bar */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 200,
        background: 'linear-gradient(135deg, #1A3A6B 0%, #267a96 100%)',
        padding: '0 32px',
        height: 52,
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
            TapLearnGo
          </div>
          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {reportName}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 16px 60px' }}>
        {error ? (
          <div style={{
            textAlign: 'center', padding: 60, background: '#fff', borderRadius: 10,
            border: '1px solid #fde8e8', color: '#D94040', fontSize: 15,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            {error}
          </div>
        ) : !reportData ? (
          <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
        ) : (
          <MultiCampaignReport
            aggregates={reportData}
            loading={false}
            formatPercentDisplay={formatPercentDisplay}
            formatNumberDisplay={formatNumberDisplay}
            percentValue={percentValue}
            reportName={reportName}
            savedReportId={savedReportId}
            initialAiInsights={initialAiInsights}
            reportLabel={pdfCustomisation.reportLabel}
            networkName={pdfCustomisation.networkName}
            orgLabel={pdfCustomisation.orgLabel}
            orgLabelPlural={pdfCustomisation.orgLabelPlural}
            clientLogoUrl={pdfCustomisation.clientLogoUrl}
          />
        )}
      </div>
    </div>
  );
};

export default ReportViewer;
